import { investorApi } from '@core/api/httpClient'
import { config } from '@core/config/config'
import type { AppError } from '@core/api/normalizeError'
import { logger } from '@core/logging/logger'
import { readFileNameFromContentDisposition } from '@shared/utils'
import { ORDER_ENDPOINTS } from './orderEndpointPaths'
import { parseOrderDocuments, parsePlaceOrderResponse } from './orderParser'
import { fallbackReceiptFileName } from '../utils/orderDocumentUtils'
import type {
  PlaceOrderPayloadDto,
  PlaceOrderResponseDto,
  OrderDetailResponseDto,
  OrderHistoryParams,
  OrderHistoryResponseDto,
  OrderHistoryFiltersResponseDto,
  CalculateFeesResponseDto,
} from './orderContracts'
import type {
  OrderDocument,
  OrderDocumentFile,
  PlaceOrderInput,
  PlacedOrder,
} from '../types/orderTypes'

export async function placeOrder(input: PlaceOrderInput): Promise<PlacedOrder> {
  const payload: PlaceOrderPayloadDto = {
    offeringId: input.offeringId,
    tokenQuantity: input.tokenQuantity,
    idempotencyKey: input.idempotencyKey,
    acceptedTerms: input.acceptedTerms,
  }
  const { data } = await investorApi.post<PlaceOrderResponseDto>(ORDER_ENDPOINTS.ORDERS, payload)
  return parsePlaceOrderResponse(data)
}

export async function getOrderDetail(orderId: string): Promise<OrderDetailResponseDto> {
  const { data } = await investorApi.get<OrderDetailResponseDto>(
    ORDER_ENDPOINTS.ORDER_DETAIL.replace('{orderId}', orderId),
  )
  return data
}

export async function cancelOrder(orderId: string): Promise<void> {
  await investorApi.post(ORDER_ENDPOINTS.CANCEL_ORDER.replace('{orderId}', orderId))
}

// Repeated keys (`status=a&status=b`) — the history DTO's array shape; axios'
// default `status[]=` brackets would not bind.
const serializeHistoryParams = ({ status, ...rest }: OrderHistoryParams): URLSearchParams => {
  const params = new URLSearchParams()
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== '') params.set(key, String(value))
  })
  status?.forEach((value) => params.append('status', value))
  return params
}

export async function getOrderHistory(
  params: OrderHistoryParams,
): Promise<OrderHistoryResponseDto> {
  const { data } = await investorApi.get<OrderHistoryResponseDto>(ORDER_ENDPOINTS.ORDER_HISTORY, {
    params: serializeHistoryParams(params),
  })
  return data
}

export async function getOrderHistoryFilters(): Promise<OrderHistoryFiltersResponseDto> {
  const { data } = await investorApi.get<OrderHistoryFiltersResponseDto>(
    ORDER_ENDPOINTS.ORDER_HISTORY_FILTERS,
  )
  return data
}

export async function calculateFees(
  offeringId: string,
  tokenQuantity: number,
): Promise<CalculateFeesResponseDto> {
  const { data } = await investorApi.get<CalculateFeesResponseDto>(ORDER_ENDPOINTS.CALCULATE_FEES, {
    params: { offeringId, tokenQuantity },
  })
  // A 200 can still carry `success: false` (e.g. PROPERTY_INVEST_MAX_ALLOCATION_REACHED);
  // surface it as the same AppError a 4xx would produce so the drawer treats both alike.
  if (data?.success === false) {
    const failure: AppError = {
      message: data.message || 'We couldn’t price this investment.',
      errorCode: data.errorCode,
    }
    throw failure
  }
  return data
}

const PDF_MIME_TYPE = 'application/pdf'

/** Content-Length when JS can read it (CORS must expose it), else the blob's own size. */
const sizeOf = (header: unknown, blob: Blob): number | null => {
  const fromHeader = Number(header)
  if (Number.isFinite(fromHeader) && fromHeader > 0) return fromHeader
  return blob.size > 0 ? blob.size : null
}

/**
 * The order receipt, rendered server-side as a PDF on every call. Fetched as
 * a blob through the authenticated client — the endpoint needs the bearer
 * token, so it can never be a plain link.
 */
export async function getOrderReceipt(
  orderId: string,
  orderReference: string,
): Promise<OrderDocumentFile> {
  const response = await investorApi.get<Blob>(
    ORDER_ENDPOINTS.ORDER_RECEIPT.replace('{orderId}', orderId),
    { responseType: 'blob' },
  )

  const fileName = readFileNameFromContentDisposition(response.headers['content-disposition'])
  if (!fileName) {
    logger.warn(
      'Order receipt: Content-Disposition not readable; the backend must list it in Access-Control-Expose-Headers. Using fallback file name.',
    )
  }

  return {
    blob: response.data,
    fileName: fileName ?? fallbackReceiptFileName(orderReference),
    sizeBytes: sizeOf(response.headers['content-length'], response.data),
    mimeType: PDF_MIME_TYPE,
  }
}

export async function getOrderDocuments(orderId: string): Promise<OrderDocument[]> {
  const { data } = await investorApi.get<unknown>(
    ORDER_ENDPOINTS.ORDER_DOCUMENTS.replace('{orderId}', orderId),
  )
  return parseOrderDocuments(data)
}

// Our own origins get the bearer token; any other host (the CDN) gets no
// Authorization header at all — the token must never leave our services.
const OWN_ORIGINS = new Set(
  [config.investorApiUrl, config.authApiUrl].map((url) => new URL(url).origin),
)

const isOwnOrigin = (url: string): boolean => {
  try {
    return OWN_ORIGINS.has(new URL(url).origin)
  } catch {
    return false
  }
}

/**
 * The document behind a documents-row `url`. Own-origin links go through the
 * authenticated client (bearer + 401 refresh); CDN links use a plain fetch
 * with no credentials. A CORS failure on the CDN rejects — the caller can
 * fall back to opening the public link in a new tab.
 */
export async function getOrderDocumentFile(
  document: OrderDocument,
  fallbackFileName: string,
): Promise<OrderDocumentFile> {
  const fileName = document.fileName || fallbackFileName

  if (isOwnOrigin(document.url)) {
    const response = await investorApi.get<Blob>(document.url, { responseType: 'blob' })
    return {
      blob: response.data,
      fileName,
      sizeBytes: document.sizeBytes ?? sizeOf(response.headers['content-length'], response.data),
      mimeType: document.mimeType,
    }
  }

  const response = await fetch(document.url, { credentials: 'omit' })
  if (!response.ok) {
    const failure: AppError = {
      message: `Document fetch failed (${response.status})`,
      statusCode: response.status,
    }
    throw failure
  }
  const blob = await response.blob()
  return {
    blob,
    fileName,
    sizeBytes: document.sizeBytes ?? sizeOf(response.headers.get('content-length'), blob),
    mimeType: document.mimeType,
  }
}
